package br.com.htms.inspeccaocampo.data

enum class RemAbrangencia(val id: String, val rotulo: String) {
    HTMS("HTMS", "HTMS"),
    SUBCONTRATADOS("SUBCONTRATADOS", "Subcontratados");

    companion object {
        fun fromId(raw: String?): RemAbrangencia =
            entries.find { it.id.equals(raw, ignoreCase = true) } ?: HTMS
    }
}

data class RemAusencia(
    val nome: String = "",
    val motivo: String = "",
    val inicio: String = "",
    val fim: String = "",
    val horas: Double? = null
) {
    companion object {
        val MOTIVOS = listOf(
            "Afastamento médico por acidente do trabalho",
            "Atestado médico",
            "Falta injustificada",
            "Falta justificada"
        )
    }
}

fun List<RemAusencia>.somaHoras(): Double? {
    val hs = mapNotNull { it.horas }
    return if (hs.isEmpty()) null else hs.sum()
}

data class RemOcorrenciasBreakdown(
    val comAfastamento: Double? = null,
    val semAfastamento: Double? = null,
    val simplesAtendimentoAmbulatorial: Double? = null,
    val diasPerdidosNoMes: Double? = null,
    val diasTransportados: Double? = null,
    val diasDebitados: Double? = null
)

/**
 * Dados de UMA empresa subcontratada (aba na UI).
 * Indicadores e nomes ficam por empresa; o Excel agrega no bloco SUB do REM.
 */
data class RemEmpresaBloco(
    val nome: String = "",
    val numeroColaboradores: Double? = null,
    val hher: Double? = null,
    val homemHoraTreinamento: Double? = null,
    val horaAbsenteismoMedico: Double? = null,
    val numeroCat: Double? = null,
    val ocorrenciasComLesao: RemOcorrenciasBreakdown = RemOcorrenciasBreakdown(),
    val ocorrenciasTrajeto: RemOcorrenciasBreakdown = RemOcorrenciasBreakdown(),
    val danosPropriedade: Double? = null,
    val nomesColaboradores: List<String> = listOf(""),
    val ausencias: List<RemAusencia> = emptyList()
) {
    fun rotuloAba(idx: Int): String {
        val n = nome.trim()
        return if (n.isNotEmpty()) n.take(22) else "Empresa ${idx + 1}"
    }
}

/**
 * Valores de INPUT de um mes (formulas TFSA/TFCA/totais ficam no Excel).
 */
data class RemMonthPayload(
    val numeroColaboradores: Double? = null,
    val hher: Double? = null,
    val homemHoraTreinamento: Double? = null,
    val horaAbsenteismoMedico: Double? = null,
    val numeroCat: Double? = null,
    val ocorrenciasComLesao: RemOcorrenciasBreakdown = RemOcorrenciasBreakdown(),
    val ocorrenciasTrajeto: RemOcorrenciasBreakdown = RemOcorrenciasBreakdown(),
    val danosPropriedade: Double? = null,
    /** Lista legada de nomes; espelha [empresasBlocos].nome. */
    val empresas: List<String> = listOf(""),
    /** Nomes dos colaboradores (HTMS ou espelho da aba ativa em SUB). */
    val nomesColaboradores: List<String> = listOf(""),
    /** Justificativas de absenteísmo. As horas somadas alimentam [horaAbsenteismoMedico]. */
    val ausencias: List<RemAusencia> = emptyList(),
    /** Apenas SUBCONTRATADOS - coluna AA do template (join de empresas + nomes). */
    val descricaoEmpresas: String = "",
    /**
     * Abas de subcontratadas (uma por empresa). Vazio = legado (empresas + campos flat).
     */
    val empresasBlocos: List<RemEmpresaBloco> = emptyList()
) {
    fun empresasParaExcel(): String =
        empresasBlocosNormalizados()
            .map { it.nome.trim() }
            .filter { it.isNotEmpty() }
            .joinToString("; ")
            .ifBlank {
                empresas.map { it.trim() }.filter { it.isNotEmpty() }.joinToString("; ")
                    .ifBlank { descricaoEmpresas.trim() }
            }

    fun nomesParaTexto(): String =
        nomesColaboradores.map { it.trim() }.filter { it.isNotEmpty() }.joinToString("; ")

    /** Grava as justificativas e faz a soma das horas virar o total de absenteísmo. */
    fun comAusencias(lista: List<RemAusencia>): RemMonthPayload {
        val horasLinha = lista.somaHoras()
        return copy(
            ausencias = lista,
            nomesColaboradores = lista.map { it.nome }.ifEmpty { listOf("") },
            horaAbsenteismoMedico = horasLinha ?: horaAbsenteismoMedico
        )
    }

    /**
     * Normaliza blocos: usa [empresasBlocos] se houver; senao migra de [empresas] + campos flat.
     */
    fun empresasBlocosNormalizados(): List<RemEmpresaBloco> {
        if (empresasBlocos.isNotEmpty()) {
            return empresasBlocos.ifEmpty { listOf(RemEmpresaBloco()) }
        }
        val nomesEmp = empresas.map { it.trim() }.let { list ->
            if (list.any { it.isNotEmpty() }) list
            else if (descricaoEmpresas.isNotBlank()) {
                descricaoEmpresas.split(';').map { it.trim() }.filter { it.isNotEmpty() }
            } else emptyList()
        }
        if (nomesEmp.isEmpty()) {
            return listOf(
                RemEmpresaBloco(
                    nome = "",
                    numeroColaboradores = numeroColaboradores,
                    hher = hher,
                    homemHoraTreinamento = homemHoraTreinamento,
                    horaAbsenteismoMedico = horaAbsenteismoMedico,
                    numeroCat = numeroCat,
                    ocorrenciasComLesao = ocorrenciasComLesao,
                    ocorrenciasTrajeto = ocorrenciasTrajeto,
                    danosPropriedade = danosPropriedade,
                    nomesColaboradores = nomesColaboradores.ifEmpty { listOf("") },
                    ausencias = ausencias
                )
            )
        }
        return nomesEmp.mapIndexed { i, nome ->
            if (i == 0) {
                RemEmpresaBloco(
                    nome = nome,
                    numeroColaboradores = numeroColaboradores,
                    hher = hher,
                    homemHoraTreinamento = homemHoraTreinamento,
                    horaAbsenteismoMedico = horaAbsenteismoMedico,
                    numeroCat = numeroCat,
                    ocorrenciasComLesao = ocorrenciasComLesao,
                    ocorrenciasTrajeto = ocorrenciasTrajeto,
                    danosPropriedade = danosPropriedade,
                    nomesColaboradores = nomesColaboradores.ifEmpty { listOf("") },
                    ausencias = ausencias
                )
            } else {
                RemEmpresaBloco(nome = nome)
            }
        }
    }

    fun blocoAt(idx: Int): RemEmpresaBloco {
        val blocs = empresasBlocosNormalizados()
        return blocs.getOrElse(idx.coerceIn(0, blocs.lastIndex.coerceAtLeast(0))) {
            RemEmpresaBloco()
        }
    }

    /** Grava o bloco na posicao e espelha nomes em [empresas] / descricao. */
    fun withBlocoAt(idx: Int, bloco: RemEmpresaBloco): RemMonthPayload {
        val blocs = empresasBlocosNormalizados().toMutableList()
        val i = idx.coerceIn(0, (blocs.size - 1).coerceAtLeast(0))
        while (blocs.size <= i) blocs.add(RemEmpresaBloco())
        blocs[i] = bloco
        return copy(
            empresasBlocos = blocs,
            empresas = blocs.map { it.nome }.ifEmpty { listOf("") },
            descricaoEmpresas = blocs.map { it.nome.trim() }.filter { it.isNotEmpty() }
                .joinToString("; "),
            numeroColaboradores = bloco.numeroColaboradores,
            hher = bloco.hher,
            homemHoraTreinamento = bloco.homemHoraTreinamento,
            horaAbsenteismoMedico = bloco.horaAbsenteismoMedico,
            numeroCat = bloco.numeroCat,
            ocorrenciasComLesao = bloco.ocorrenciasComLesao,
            ocorrenciasTrajeto = bloco.ocorrenciasTrajeto,
            danosPropriedade = bloco.danosPropriedade,
            nomesColaboradores = bloco.nomesColaboradores.ifEmpty { listOf("") },
            ausencias = bloco.ausencias
        )
    }

    fun adicionarEmpresaBloco(): RemMonthPayload {
        val blocs = empresasBlocosNormalizados() + RemEmpresaBloco()
        return copy(
            empresasBlocos = blocs,
            empresas = blocs.map { it.nome }
        )
    }

    fun removerEmpresaBloco(idx: Int): RemMonthPayload {
        val blocs = empresasBlocosNormalizados().toMutableList()
        if (blocs.size <= 1) return this
        if (idx in blocs.indices) blocs.removeAt(idx)
        val keep = blocs.ifEmpty { mutableListOf(RemEmpresaBloco()) }
        val first = keep.first()
        return copy(
            empresasBlocos = keep,
            empresas = keep.map { it.nome }.ifEmpty { listOf("") },
            descricaoEmpresas = keep.map { it.nome.trim() }.filter { it.isNotEmpty() }
                .joinToString("; "),
            numeroColaboradores = first.numeroColaboradores,
            hher = first.hher,
            homemHoraTreinamento = first.homemHoraTreinamento,
            horaAbsenteismoMedico = first.horaAbsenteismoMedico,
            numeroCat = first.numeroCat,
            ocorrenciasComLesao = first.ocorrenciasComLesao,
            ocorrenciasTrajeto = first.ocorrenciasTrajeto,
            danosPropriedade = first.danosPropriedade,
            nomesColaboradores = first.nomesColaboradores.ifEmpty { listOf("") },
            ausencias = first.ausencias
        )
    }

    /** Espelha bloco [idx] nos campos flat (UI HTMS-style). */
    fun espelharBlocoNosCamposFlat(idx: Int): RemMonthPayload {
        val b = blocoAt(idx)
        return copy(
            empresasBlocos = empresasBlocosNormalizados(),
            empresas = empresasBlocosNormalizados().map { it.nome }.ifEmpty { listOf("") },
            numeroColaboradores = b.numeroColaboradores,
            hher = b.hher,
            homemHoraTreinamento = b.homemHoraTreinamento,
            horaAbsenteismoMedico = b.horaAbsenteismoMedico,
            numeroCat = b.numeroCat,
            ocorrenciasComLesao = b.ocorrenciasComLesao,
            ocorrenciasTrajeto = b.ocorrenciasTrajeto,
            danosPropriedade = b.danosPropriedade,
            nomesColaboradores = b.nomesColaboradores.ifEmpty { listOf("") },
            ausencias = b.ausencias,
            descricaoEmpresas = empresasBlocosNormalizados()
                .map { it.nome.trim() }.filter { it.isNotEmpty() }.joinToString("; ")
        )
    }
}

data class RemMonthRow(
    val id: String? = null,
    val abrangencia: RemAbrangencia = RemAbrangencia.HTMS,
    val mes: Int = 1,
    val payload: RemMonthPayload = RemMonthPayload(),
    val updatedAt: Long? = null
)

data class RemDossier(
    val id: String,
    val userId: String? = null,
    val username: String? = null,
    val obra: String,
    val ano: Int,
    val createdAt: Long? = null,
    val updatedAt: Long? = null,
    val months: List<RemMonthRow> = emptyList()
)

/**
 * Estado de UI do formulario REM (nao e InspecaoCompleta).
 */
data class FormRemSeguranca(
    val dossierId: String? = null,
    val obra: String = "",
    val ano: Int = 2026,
    val abrangencia: RemAbrangencia = RemAbrangencia.HTMS,
    val mes: Int = 1,
    val payload: RemMonthPayload = RemMonthPayload(),
    /** Indice da aba de empresa subcontratada ativa. */
    val empresaTabIndex: Int = 0,
    val monthsCache: List<RemMonthRow> = emptyList(),
    val dirty: Boolean = false,
    val lastSyncedAt: Long? = null,
    /** Mês já salvo no servidor — TST não pode alterar (somente admin). */
    val readOnly: Boolean = false
) {
    fun payloadFor(abr: RemAbrangencia, mesSel: Int): RemMonthPayload =
        monthsCache.find { it.abrangencia == abr && it.mes == mesSel }?.payload
            ?: if (abrangencia == abr && mes == mesSel) payload else RemMonthPayload()
}

object RemMeses {
    val rotulos = listOf(
        "JAN", "FEV", "MAR", "ABR", "MAI", "JUN",
        "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"
    )
    fun rotulo(mes: Int): String = rotulos.getOrElse(mes - 1) { mes.toString() }
}
